/* JC Select - Dogwood custom dropdown.
   Markup comes from snippets/jc-select.liquid (forms) or from
   snippets/variant-main-picker.liquid (product variant dropdowns).
   This upgrades <jc-select>: hides the native <select>, shows the
   trigger button, opens the branded list, keeps the native select's
   value in sync (so forms submit it and Horizon's 'change' listeners
   still fire), handles keyboard use like a native select, and checks
   "required" on submit.

   Built to survive Horizon rebuilding the markup underneath it: every
   listener sits on the <jc-select> host and the inner elements are
   looked up on demand, and a MutationObserver re-syncs the trigger
   text whenever the server-rendered select changes (variant updates
   morph the picker on every choice). */
(function () {
  if (customElements.get('jc-select')) return;

  class JCSelect extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };

      this.addEventListener('click', (e) => this.onClick(e), opts);
      this.addEventListener('keydown', (e) => this.onKeydown(e), opts);
      this.addEventListener('mousemove', (e) => {
        const opt = e.target.closest('[role="option"]');
        if (opt && this.contains(opt)) this.setActive(opt, false);
      }, opts);
      this.addEventListener('change', (e) => {
        if (e.target === this.native) this.syncFromNative();
      }, opts);

      this.onDocClick = (e) => { if (!this.contains(e.target)) this.close(false); };
      this.onSubmit = (e) => {
        const alreadyStopped = e.defaultPrevented;
        if (!this.validate()) {
          e.preventDefault();
          if (!alreadyStopped && this.trigger) this.trigger.focus();
        }
      };

      this.observer = new MutationObserver(() => this.syncFromNative());
      this.observer.observe(this, { childList: true, subtree: true, attributes: true, characterData: true });

      this.syncFromNative();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.abort = null;
      document.removeEventListener('click', this.onDocClick);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      this.form = null;
      if (this.observer) this.observer.disconnect();
    }

    /* Inner elements are looked up on demand: Horizon may swap them out. */
    get native() { return this.querySelector('.jc-select__native'); }
    get trigger() { return this.querySelector('.jc-select__trigger'); }
    get list() { return this.querySelector('.jc-select__list'); }
    get valueEl() { return this.querySelector('.jc-select__value'); }
    get errorEl() { return this.querySelector('.jc-select__error'); }
    get options() { const l = this.list; return l ? Array.from(l.querySelectorAll('[role="option"]')) : []; }
    get isOpen() { const t = this.trigger; return !!t && t.getAttribute('aria-expanded') === 'true'; }
    get active() { const l = this.list; return l ? l.querySelector('.is-active') : null; }

    bindForm() {
      const n = this.native;
      const form = n ? n.form : null;
      if (form === this.form) return;
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      this.form = form;
      if (form) form.addEventListener('submit', this.onSubmit);
    }

    onClick(e) {
      const opt = e.target.closest('[role="option"]');
      if (opt && this.contains(opt)) {
        this.select(opt);
        this.close(true);
        return;
      }
      if (e.target.closest('.jc-select__trigger')) this.toggle();
    }

    open() {
      const t = this.trigger, l = this.list, opts = this.options;
      if (!t || !l || this.isOpen || !opts.length) return;
      l.hidden = false;
      t.setAttribute('aria-expanded', 'true');
      this.placeList();
      const selected = opts.find((o) => o.getAttribute('aria-selected') === 'true');
      this.setActive(selected || opts[0]);
      setTimeout(() => document.addEventListener('click', this.onDocClick), 0);
    }

    close(refocus) {
      const t = this.trigger, l = this.list;
      if (!t || !l || !this.isOpen) return;
      l.hidden = true;
      t.setAttribute('aria-expanded', 'false');
      t.removeAttribute('aria-activedescendant');
      this.options.forEach((o) => o.classList.remove('is-active'));
      document.removeEventListener('click', this.onDocClick);
      if (refocus) t.focus();
    }

    toggle() { this.isOpen ? this.close(true) : this.open(); }

    /* Open upward when there is not enough room below the trigger. */
    placeList() {
      const t = this.trigger, l = this.list;
      if (!t || !l) return;
      const rect = t.getBoundingClientRect();
      const needed = l.offsetHeight + 16;
      const below = window.innerHeight - rect.bottom;
      this.classList.toggle('jc-select--up', below < needed && rect.top > needed);
    }

    setActive(opt, scroll) {
      const t = this.trigger;
      this.options.forEach((o) => o.classList.toggle('is-active', o === opt));
      if (t) t.setAttribute('aria-activedescendant', opt.id);
      if (scroll !== false) opt.scrollIntoView({ block: 'nearest' });
    }

    select(opt) {
      const n = this.native;
      if (!n) return;
      n.value = opt.dataset.value;
      n.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Mirror the native select into the trigger and list. Only writes
       when something differs, so the MutationObserver cannot loop. */
    syncFromNative() {
      const n = this.native;
      if (!n) return;
      this.bindForm();
      const value = n.value;
      const opts = this.options;
      const match = opts.find((o) => o.dataset.value === value);
      opts.forEach((o) => {
        const want = o === match ? 'true' : 'false';
        if (o.getAttribute('aria-selected') !== want) o.setAttribute('aria-selected', want);
      });
      const chosen = n.options[n.selectedIndex];
      const placeholder = n.options.length && n.options[0].value === '' ? n.options[0].text.trim() : '';
      const text = match ? match.textContent.trim() : (chosen ? chosen.text.trim() : placeholder);
      const v = this.valueEl;
      if (v && v.textContent !== text) v.textContent = text;
      const hasValue = value !== '';
      if (this.classList.contains('jc-select--has-value') !== hasValue) this.classList.toggle('jc-select--has-value', hasValue);
      if (hasValue) this.clearError();
    }

    move(step) {
      const opts = this.options;
      const i = opts.indexOf(this.active);
      const next = Math.min(Math.max(i + step, 0), opts.length - 1);
      if (opts[next]) this.setActive(opts[next]);
    }

    /* Typing a letter jumps to the next option starting with it. */
    typeahead(char, selectNow) {
      const c = char.toLowerCase();
      const opts = this.options;
      const start = opts.indexOf(this.active);
      const ordered = opts.slice(start + 1).concat(opts.slice(0, start + 1));
      const hit = ordered.find((o) => o.textContent.trim().toLowerCase().startsWith(c));
      if (!hit) return;
      if (selectNow) this.select(hit); else this.setActive(hit);
    }

    onKeydown(e) {
      if (!e.target.closest('.jc-select__trigger')) return;
      const k = e.key;
      const opts = this.options;
      if (!this.isOpen) {
        if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ') {
          e.preventDefault();
          this.open();
        } else if (k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          this.typeahead(k, true);
        }
        return;
      }
      switch (k) {
        case 'ArrowDown': e.preventDefault(); this.move(1); break;
        case 'ArrowUp': e.preventDefault(); this.move(-1); break;
        case 'Home': e.preventDefault(); if (opts[0]) this.setActive(opts[0]); break;
        case 'End': e.preventDefault(); if (opts.length) this.setActive(opts[opts.length - 1]); break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (this.active) this.select(this.active);
          this.close(true);
          break;
        case 'Escape': e.preventDefault(); this.close(true); break;
        case 'Tab':
          if (this.active) this.select(this.active);
          this.close(false);
          break;
        default:
          if (k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) this.typeahead(k, false);
      }
    }

    validate() {
      const n = this.native;
      if (this.dataset.required !== 'true' || !n || n.value) return true;
      this.showError(this.dataset.requiredMessage || 'Please choose an option');
      return false;
    }

    showError(message) {
      const t = this.trigger, err = this.errorEl;
      this.classList.add('jc-select--invalid');
      if (t) t.setAttribute('aria-invalid', 'true');
      if (err) { err.textContent = message; err.hidden = false; }
    }

    clearError() {
      if (!this.classList.contains('jc-select--invalid')) return;
      const t = this.trigger, err = this.errorEl;
      this.classList.remove('jc-select--invalid');
      if (t) t.removeAttribute('aria-invalid');
      if (err) { err.textContent = ''; err.hidden = true; }
    }
  }

  customElements.define('jc-select', JCSelect);
})();
